import { Navigate, type RouteObject } from "react-router-dom"

import { REPORT_CATALOG_TABS, ReportCatalogTab } from "@/features/reports"
import {
  createAdminParamRedirectComponent,
  createAdminRedirectComponent,
  createStaticRedirectComponent,
  ParishComingSoonRoute,
} from "@/app/router/route-helpers"
import { lazyNamedRouteModule, lazyPage } from "@/app/router/route-module-registry"
import { ReconciliationSessionIndexRedirect, createAdminCreateRedirectComponent } from "@/app/router/route-helpers"
import { CreateKind } from "@/features/create-overlay"
import {
  BudgetCreateRedirect,
  PledgeCreateRedirect,
  TransactionCreateRedirect,
  TransferCreateRedirect,
  TransferRequestCreateRedirect,
} from "@/app/router/finance-create-redirects"
import { RequireCapability } from "@/app/router/require-capability"
import type { TenantRouteHandle } from "@/features/dashboard"
import { FinanceSetupGuard } from "@/features/finance"

const lazyAuthLayoutRoute = lazyNamedRouteModule("./auth-layout-route.tsx", "AuthLayoutRoute")
const lazyNotFoundRoute = lazyNamedRouteModule("./not-found-route.tsx", "NotFoundRoute")
const lazyMembersLayoutRoute = lazyNamedRouteModule("./members-layout-route.tsx", "MembersLayoutRoute")
const lazyGlobalFinanceLayoutRoute = lazyNamedRouteModule(
  "./global-finance-layout-route.tsx",
  "GlobalFinanceLayoutRoute",
)
const lazyFinanceAccountsFundsLayoutRoute = lazyNamedRouteModule(
  "./finance-accounts-funds-layout-route.tsx",
  "FinanceAccountsFundsLayoutRoute",
)
const lazySettingsLayoutRoute = lazyNamedRouteModule("./settings-layout-route.tsx", "SettingsLayoutRoute")
const lazyMyBranchLayoutRoute = lazyNamedRouteModule("./my-branch-layout-route.tsx", "MyBranchLayoutRoute")
const lazyReportsLayoutRoute = lazyNamedRouteModule("./reports-layout-route.tsx", "ReportsLayoutRoute")
const lazyFinanceSettingsLayoutRoute = lazyNamedRouteModule(
  "./finance-settings-layout-route.tsx",
  "FinanceSettingsLayoutRoute",
)
const lazySystemSettingsLayoutRoute = lazyNamedRouteModule(
  "./system-settings-layout-route.tsx",
  "SystemSettingsLayoutRoute",
)
const lazyOrganizationSettingsLayoutRoute = lazyNamedRouteModule(
  "./organization-settings-layout-route.tsx",
  "OrganizationSettingsLayoutRoute",
)
const lazyAdminLayoutRoute = lazyNamedRouteModule("./admin-layout-route.tsx", "AdminLayoutRoute")
const lazySubscriptionLayoutRoute = lazyNamedRouteModule(
  "../../features/subscription/admin-route-layouts.tsx",
  "SubscriptionLayoutRoute",
)
const lazyActivityDetailLayoutRoute = lazyNamedRouteModule(
  "../../features/activities/admin-route-layouts.tsx",
  "ActivityDetailLayoutRoute",
)
const lazyBudgetsOutletLayoutRoute = lazyNamedRouteModule(
  "../../features/budgeting/admin-route-layouts.tsx",
  "BudgetsOutletLayoutRoute",
)
const lazyBudgetsShellLayoutRoute = lazyNamedRouteModule(
  "../../features/budgeting/admin-route-layouts.tsx",
  "BudgetsShellLayoutRoute",
)
const lazyFormDetailLayoutRoute = lazyNamedRouteModule("../../features/forms/admin-route-layouts.tsx", "FormDetailLayoutRoute")
const lazyFormDetailIndexRedirect = lazyNamedRouteModule(
  "../../features/forms/admin-route-layouts.tsx",
  "FormDetailIndexRedirect",
)
const lazyDepartmentDetailLayoutRoute = lazyNamedRouteModule(
  "../../features/departments/admin-route-layouts.tsx",
  "DepartmentDetailLayoutRoute",
)
const lazyDepartmentDetailIndexRedirect = lazyNamedRouteModule(
  "../../features/departments/admin-route-layouts.tsx",
  "DepartmentDetailIndexRedirect",
)
const lazyBranchDetailLayoutRoute = lazyNamedRouteModule(
  "../../features/church/config/branches/admin-route-layouts.tsx",
  "BranchDetailLayoutRoute",
)
const lazyBranchDetailIndexRedirect = lazyNamedRouteModule(
  "../../features/church/config/branches/admin-route-layouts.tsx",
  "BranchDetailIndexRedirect",
)
const lazyLegacyBranchDetailRedirect = lazyNamedRouteModule(
  "../../features/church/config/branches/admin-route-layouts.tsx",
  "LegacyBranchDetailRedirect",
)

function createSelectedBranchSettingsRoutes(): RouteObject[] {
  return [
    // Branch self-service moved to "My Branch" (Overview + Team) in the main app sidebar; Groups moved to
    // Members. The old Branch Settings landing + its tabs redirect to their new homes.
    { index: true, Component: createAdminRedirectComponent("/my-branch") },
    { path: "groups", Component: createAdminRedirectComponent("/members/groups") },
    { path: "details", Component: createAdminRedirectComponent("/my-branch") },
    // Finance categories + accounts moved into the Finance section; old settings paths redirect there.
    { path: "finance-categories", Component: createAdminRedirectComponent("/finance/categories") },
    { path: "accounts", Component: createAdminRedirectComponent("/finance/accounts") },
    // Templates (receipt template editor) is retired for now — it will be redesigned and rehomed later.
    { path: "templates", Component: createAdminRedirectComponent("/my-branch") },
    { path: "branch-administration", Component: createAdminRedirectComponent("/my-branch/team") },
    { path: "templates/receipt", Component: createAdminRedirectComponent("/my-branch") },
    {
      path: "appearance",
      lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/settings/appearance/page.tsx"),
    },
    { path: "branch", Component: createAdminRedirectComponent("/my-branch") },
    { path: "branch/groups", Component: createAdminRedirectComponent("/members/groups") },
    { path: "branch/finance-categories", Component: createAdminRedirectComponent("/finance/categories") },
    { path: "branch/accounts", Component: createAdminRedirectComponent("/finance/accounts") },
    { path: "branch/finance", Component: createAdminRedirectComponent("/my-branch") },
    { path: "branch/templates", Component: createAdminRedirectComponent("/my-branch") },
    { path: "branch/templates/receipt", Component: createAdminRedirectComponent("/my-branch") },
    {
      path: "bug-report",
      lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/settings/bug-report/page.tsx"),
    },
    {
      path: "bug-report/manage",
      lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/settings/bug-report/manage/page.tsx"),
    },
    { path: "general", Component: createAdminRedirectComponent("/settings") },
    { path: "parish", Component: createAdminRedirectComponent("/settings") },
    { path: "parish/groups", Component: createAdminRedirectComponent("/settings") },
    { path: "parish/accounts", Component: createAdminRedirectComponent("/finance/accounts") },
    {
      /*
       * Reachable, deliberately, even with no billing backend.
       *
       * The gate moved INSIDE the page (`subscription-page-content.tsx`): how much trial is left and which
       * plan you are on cost nothing to show and are the questions this page is opened to answer, while the
       * plan-change control and its history stay behind `billing.manage` until there is something to bill
       * against. Gating the whole route instead left the tab in the tab bar bouncing back to Organisation —
       * a dead affordance, and the trial card rendered somewhere nobody could open.
       */
      path: "subscription",
      // Offre (index) and Historique are real path segments, so each sub-tab is deep-linkable.
      lazy: lazySubscriptionLayoutRoute,
      children: [
        { index: true, lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/settings/subscription/page.tsx") },
        { path: "history", lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/settings/subscription/history/page.tsx") },
      ],
    },
    {
      path: "system",
      lazy: lazySystemSettingsLayoutRoute,
      children: [
        {
          index: true,
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/settings/system/page.tsx"),
        },
        {
          path: "branches",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/settings/system/branches/page.tsx"),
        },
        {
          path: "team",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/settings/system/team/page.tsx"),
        },
        {
          path: "roles",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/settings/system/roles/page.tsx"),
        },
        {
          path: "invitations",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/settings/system/invitations/page.tsx"),
        },
      ],
    },
  ]
}

/**
 * "Settings" (workspace governance) shown in the Organization context: Organization, Team, Roles &
 * Permissions, Subscription. Reuses the existing organization/subscription page modules; the tab bar is
 * provided by {@link OrganizationSettingsLayoutRoute}. `/:ws/settings` lands on the Organization tab.
 */
function createOrganizationSettingsRoutes(): RouteObject[] {
  return [
    { index: true, Component: createAdminRedirectComponent("/settings/organization") },
    { path: "general", Component: createAdminRedirectComponent("/settings/organization") },
    { path: "organization", lazy: lazyPage("../w/[workspaceSlug]/organization/page.tsx") },
    { path: "team", lazy: lazyPage("../w/[workspaceSlug]/organization/team/page.tsx") },
    { path: "roles", lazy: lazyPage("../w/[workspaceSlug]/organization/roles/page.tsx") },
    {
      /*
       * Reachable, deliberately, even with no billing backend.
       *
       * The gate moved INSIDE the page (`subscription-page-content.tsx`): how much trial is left and which
       * plan you are on cost nothing to show and are the questions this page is opened to answer, while the
       * plan-change control and its history stay behind `billing.manage` until there is something to bill
       * against. Gating the whole route instead left the tab in the tab bar bouncing back to Organisation —
       * a dead affordance, and the trial card rendered somewhere nobody could open.
       */
      path: "subscription",
      // Offre (index) and Historique are real path segments, so each sub-tab is deep-linkable.
      lazy: lazySubscriptionLayoutRoute,
      children: [
        { index: true, lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/settings/subscription/page.tsx") },
        { path: "history", lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/settings/subscription/history/page.tsx") },
      ],
    },
  ]
}

type AdminRouteContext = "branch" | "organization"

function createAdminBranchChildren(context: AdminRouteContext = "branch"): RouteObject[] {
  return [
    {
      index: true,
      lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/page.tsx"),
    },
    {
      // Audit Log: workspace/parish governance surface. It must not be wrapped in the parish Coming Soon guard:
      // the sidebar exposes it at organization level and owners/settings auditors must be able to inspect it.
      path: "audit-log",
      lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/audit-log/page.tsx"),
    },
    { path: "recent-activity", Component: createAdminRedirectComponent("/audit-log") },
    {
      // Parish → Branches (branch management, promoted from My Organization). The index is the branches table;
      // selecting a branch opens the Branch Details console at :branchId (added with the console).
      path: "branches",
      children: [
        { index: true, lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/branches/page.tsx") },
        {
          // Branch Details console — read-only, tabbed. :branchId is the inspected NORMAL branch.
          path: ":branchId",
          lazy: lazyBranchDetailLayoutRoute,
          children: [
            { index: true, lazy: lazyBranchDetailIndexRedirect },
            { path: "overview", lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/branches/[branchId]/overview/page.tsx") },
            { path: "members", lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/branches/[branchId]/members/page.tsx") },
            { path: "ministers", lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/branches/[branchId]/ministers/page.tsx") },
            { path: "departments", lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/branches/[branchId]/departments/page.tsx") },
            { path: "team", lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/branches/[branchId]/team/page.tsx") },
            { path: "finance", lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/branches/[branchId]/finance/page.tsx") },
            { path: "settings", lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/branches/[branchId]/settings/page.tsx") },
          ],
        },
      ],
    },
    {
      // Parish → Ministries (organization-level ministry governance), promoted from My Organization.
      path: "ministries",
      lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/ministries/page.tsx"),
    },
    {
      path: "notifications",
      handle: { breadcrumbKey: "notifications.page.title" } satisfies TenantRouteHandle,
      lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/notifications/page.tsx"),
    },
    {
      path: "setup",
      lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/setup/page.tsx"),
    },
    {
      path: "members",
      lazy: lazyMembersLayoutRoute,
      children: [
        {
          index: true,
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/page.tsx"),
        },
        {
          path: "list",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/list/page.tsx"),
        },
        {
          path: "visitors",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/visitors/page.tsx"),
        },
        { path: "overview", Component: createAdminRedirectComponent("/members") },
        {
          path: "import",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/import/page.tsx"),
        },
        {
          // A real path segment rather than local state, so a past import is linkable.
          path: "import/history",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/import/history/page.tsx"),
        },
        {
          // One import conversation; the session id makes it resumable and linkable.
          path: "import/assistant/:sessionId",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/import/assistant/[sessionId]/page.tsx"),
        },
        {
          // Creating opens over a page now: `?kind=visitor` opens the visitor form over the visitors list, anything
          // else the member form over the members list.
          path: "create",
          Component: createAdminCreateRedirectComponent((search) =>
            search.get("kind") === CreateKind.Visitor ? CreateKind.Visitor : CreateKind.Member,
          ),
        },
        {
          path: "groups",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/groups/page.tsx"),
        },
        {
          path: "ministries",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/ministries/page.tsx"),
        },
        {
          path: "baptism",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/baptism/page.tsx"),
        },
        {
          // A campaign's own page — deep-linkable, because a baptism Sunday is worked from this roster.
          path: "baptism/cohorts/:cohortId",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/baptism/cohorts/[cohortId]/page.tsx"),
        },
        {
          /*
           * Member detail. The four sections are PATH SEGMENTS, not component state — the page renders the
           * tab bar itself and reads the active tab from the URL, so every section is linkable and survives a
           * reload. The same page component serves each child; only the trailing segment differs.
           */
          path: ":id",
          children: [
            { index: true, lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/[id]/page.tsx") },
            {
              // The phone's personal-information page (its index is the profile summary); desktop shows the
              // same Personal tab here as on the index.
              path: "personal",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/[id]/page.tsx"),
            },
            {
              path: "church-profile",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/[id]/page.tsx"),
            },
            {
              path: "relationships",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/[id]/page.tsx"),
            },
            {
              path: "journey",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/members/[id]/page.tsx"),
            },
          ],
        },
      ],
    },
    {
      // Operational Activities (ADR-0017): schedule, occurrences, programs. Finance attribution lives at /finance/activities.
      // Temporary path pending IA decision (nav entry + final URL + replacing the WIP /programmes prototype).
      path: "activities",
      children: [
        {
          index: true,
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/activities/page.tsx"),
        },
        // Creating an activity opens over the current page (the create overlay); the old route opens it on the calendar.
        { path: "create", Component: createAdminCreateRedirectComponent(() => CreateKind.Activity) },
        {
          // The table view of the page; the bare path is the month calendar. A real segment so it can be linked.
          path: "list",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/activities/list/page.tsx"),
        },
        {
          // A program's details as a dedicated, deep-linkable page (outside the activity tab layout —
          // the program has its own header/footer chrome). `?date=<occurrenceDate>` carries the occurrence.
          path: ":id/programmes/:programId",
          lazy: lazyPage(
            "../[workspaceSlug]/[branchSlug]/admin/activities/[id]/programmes/[programId]/page.tsx",
          ),
        },
        {
          path: ":id",
          lazy: lazyActivityDetailLayoutRoute,
          children: [
            {
              index: true,
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/activities/[id]/page.tsx"),
            },
            {
              path: "about",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/activities/[id]/about/page.tsx"),
            },
            {
              // An activity is operational — its schedule and programs run whether or not the books are open.
              // These three tabs are not: they take money, promise money and plan money, so they wait for the
              // same flag every other finance surface does. Only these three, so scheduling still works.
              Component: FinanceSetupGuard,
              children: [
                {
                  path: "finance",
                  lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/activities/[id]/finance/page.tsx"),
                },
                {
                  // Activity pledges are gated by the activity's own Pledges capability (a per-activity opt-in),
                  // not the global standalone-pledges feature flag — so no RequireCapability guard here.
                  path: "pledges",
                  lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/activities/[id]/pledges/page.tsx"),
                },
                {
                  path: "budget",
                  lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/activities/[id]/budget/page.tsx"),
                },
              ],
            },
            {
              path: "settings",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/activities/[id]/settings/page.tsx"),
            },
            {
              // Phone only: the "Plus" view of the activity's segmented control. Desktop shows every section as
              // a tab instead, so this path returns to the activity there.
              path: "more",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/activities/[id]/more/page.tsx"),
            },
          ],
        },
      ],
    },
    {
      path: "departments",
      children: [
        {
          index: true,
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/departments/page.tsx"),
        },
        {
          path: ":id",
          lazy: lazyDepartmentDetailLayoutRoute,
          children: [
            { index: true, lazy: lazyDepartmentDetailIndexRedirect },
            {
              path: "members",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/departments/[id]/members/page.tsx"),
            },
            {
              path: "roles",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/departments/[id]/roles/page.tsx"),
            },
            {
              // Department finance is intentionally hidden for now. Keep deep links safe.
              path: "finance/*",
              element: <Navigate to="../members" replace />,
            },
            {
              path: "activities",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/departments/[id]/activities/page.tsx"),
            },
            {
              path: "notes",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/departments/[id]/notes/page.tsx"),
            },
            {
              path: "settings",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/departments/[id]/settings/page.tsx"),
            },
          ],
        },
      ],
    },
    {
      path: "assets",
      element: <RequireCapability capability="assets.view" />,
      children: [
        { index: true, Component: createAdminRedirectComponent("/assets/items") },
        {
          path: "items",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/assets/items/page.tsx"),
        },
        // Recording an asset opens over the current page (the create overlay); the old route opens it on the list.
        // Editing stays a page. `new` is declared before `:assetId/edit` so the literal wins the match.
        { path: "items/new", Component: createAdminCreateRedirectComponent(() => CreateKind.Asset) },
        {
          path: "items/:assetId/edit",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/assets/items/[assetId]/edit/page.tsx"),
        },
        {
          path: "categories",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/assets/categories/page.tsx"),
        },
        {
          path: "history",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/assets/history/page.tsx"),
        },
      ],
    },
    {
      path: "forms",
      element: <RequireCapability capability="forms.view" />,
      children: [
        {
          index: true,
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/forms/page.tsx"),
        },
        {
          path: "create",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/forms/create/page.tsx"),
        },
        {
          // Standalone preview surface embedded by the designer in a phone-width iframe (postMessage-driven).
          path: "preview",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/forms/preview/page.tsx"),
        },
        {
          path: ":formId/edit",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/forms/[formId]/edit/page.tsx"),
        },
        {
          path: ":formId",
          lazy: lazyFormDetailLayoutRoute,
          children: [
            { index: true, lazy: lazyFormDetailIndexRedirect },
            {
              path: "submissions",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/forms/[formId]/submissions/page.tsx"),
            },
            {
              path: "insights",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/forms/[formId]/insights/page.tsx"),
            },
            {
              path: "settings",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/forms/[formId]/settings/page.tsx"),
            },
          ],
        },
      ],
    },
    {
      path: "finance",
      // A branch whose books were never opened is sent to Setup (IBA-119). A courtesy, not the enforcement:
      // the gate is in the API, at the ledger, so a deep link or a `curl` is refused whether or not this runs.
      Component: FinanceSetupGuard,
      children: [
        {
          index: true,
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/page.tsx"),
        },
        {
          // The guided Configure Finance onboarding wizard. Reachable through the guard (a branch that hasn't
          // reached the minimum foundation lands here) and directly to continue optional steps after unlock.
          path: "configure",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/configure/page.tsx"),
        },
        {
          // Legacy "Fonds des activités" surface — consolidated into the canonical /activities surface.
          // These paths now redirect; the backend API path /finance/activities is unrelated and untouched.
          path: "activities",
          children: [
            { index: true, Component: createAdminRedirectComponent("/activities") },
            { path: "list", Component: createAdminRedirectComponent("/activities") },
            { path: "overview", Component: createAdminRedirectComponent("/activities") },
            {
              path: ":id",
              children: [
                {
                  index: true,
                  Component: createAdminParamRedirectComponent((p) => `/activities/${p.id}`),
                },
                {
                  path: "finance",
                  Component: createAdminParamRedirectComponent((p) => `/activities/${p.id}/finance`),
                },
                {
                  path: "pledges",
                  Component: createAdminParamRedirectComponent((p) => `/activities/${p.id}/pledges`),
                },
                {
                  // Transactions live in the canonical activity Finance tab.
                  path: "transactions",
                  Component: createAdminParamRedirectComponent((p) => `/activities/${p.id}/finance`),
                },
              ],
            },
          ],
        },
        {
          path: "transactions",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/transactions/page.tsx"),
        },
        {
          // Old transaction create page (?kind=income|expense): now the offering / expense overlay over the list.
          path: "transactions/create",
          Component: TransactionCreateRedirect,
        },
        {
          // Cross-context transfer requests (IBA-158): the approval inbox and the sent list.
          path: "transfers",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/transfers/page.tsx"),
        },
        {
          // Create a cross-context transfer REQUEST (branch <-> parish, branch <-> branch).
          // Now the transfer request overlay over the transfers list; `?saved=<id>` still starts from that calculation.
          path: "transfers/requests/create",
          Component: TransferRequestCreateRedirect,
        },
        {
          // One transfer, in full. Declared BEFORE `/edit` so the more specific path still wins its own
          // route, and so the submitted/approved/rejected notifications have somewhere specific to land.
          path: "transfers/requests/:transferId",
          lazy: lazyPage(
            "../[workspaceSlug]/[branchSlug]/admin/finance/transfers/requests/[transferId]/page.tsx",
          ),
        },
        {
          // Edit a DRAFT transfer request. Drafts are the only editable state.
          path: "transfers/requests/:transferId/edit",
          lazy: lazyPage(
            "../[workspaceSlug]/[branchSlug]/admin/finance/transfers/requests/[transferId]/edit/page.tsx",
          ),
        },
        {
          // Old internal account-to-account transfer page: now the transfer overlay over the transactions list.
          path: "transfers/create",
          Component: TransferCreateRedirect,
        },
        {
          path: "balances",
          Component: createAdminRedirectComponent("/finance/settings/accounts"),
        },
        {
          // Accounts and Funds share ONE sidebar entry and one page with two tabs. The layout route is
          // PATHLESS on purpose: it adds the tab bar without adding a URL segment, so both keep the paths they
          // already had and every existing redirect/bookmark still lands correctly.
          lazy: lazyFinanceAccountsFundsLayoutRoute,
          children: [
            {
              // Chart of Accounts (top-level "Comptes"): one table, segmented Tous/Financiers/Revenus/Dépenses.
              // Workspace-shared; read-only here (creation is from the parish or a mono-branch workspace).
              path: "accounts",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/accounts/page.tsx"),
            },
            {
              // Funds: the real branch funds page; at parish level Funds (global vs branch-public scoping) is
              // not built yet, so it shows Coming Soon.
              path: "funds",
              element: <ParishComingSoonRoute />,
              children: [
                { index: true, lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/funds/page.tsx") },
              ],
            },
          ],
        },
        {
          // Remittances SHIPPED as Transfers — branch↔parish movement is the transfers screen. This path kept
          // only so existing links and bookmarks land on the real feature instead of a removed placeholder.
          path: "remittances",
          element: <Navigate to="../transfers" replace />,
        },
        {
          // Bank reconciliation (IBA-161): the session list plus a routed page per session. The per-session
          // URL is what a review notification links to, so it is a real route, not a selection in a list.
          path: "reconciliation",
          children: [
            {
              index: true,
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/reconciliation/page.tsx"),
            },
            {
              // Tabs are real URL segments so a review notification can link straight to a session, and
              // "look at the history" is a link somebody can send.
              path: ":sessionId",
              children: [
                { index: true, Component: ReconciliationSessionIndexRedirect },
                {
                  path: ":tab",
                  lazy: lazyPage(
                    "../[workspaceSlug]/[branchSlug]/admin/finance/reconciliation/[sessionId]/page.tsx",
                  ),
                },
              ],
            },
          ],
        },
        {
          // Finance Investigations (IBA-75): financial traceability + the investigation workspace.
          path: "investigations",
          children: [
            {
              index: true,
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/investigations/page.tsx"),
            },
            {
              // Full-page investigation workspace for one financial object (shareable, deep-linkable).
              path: "investigation/:kind/:id",
              lazy: lazyPage(
                "../[workspaceSlug]/[branchSlug]/admin/finance/investigations/investigation/page.tsx",
              ),
            },
          ],
        },
        {
          // Legacy audit routes → Investigations.
          path: "audit",
          children: [
            { index: true, Component: createAdminRedirectComponent("/finance/investigations") },
            {
              path: "investigation/:kind/:id",
              Component: createAdminParamRedirectComponent(
                (p) => `/finance/investigations/investigation/${p.kind}/${p.id}`,
              ),
            },
          ],
        },
        {
          /*
           * Finance no longer has its own Reports destination — there is one catalog, and it is top level.
           * These stay as redirects because treasurers have bookmarks and the deep links were published in
           * the app for months.
           */
          path: "reports",
          children: [
            { index: true, Component: createAdminRedirectComponent("/reports/finance") },
            { path: ":reportId/configure", Component: createAdminParamRedirectComponent((p) => `/reports/${p.reportId}/configure`) },
            { path: ":reportId/view", Component: createAdminParamRedirectComponent((p) => `/reports/${p.reportId}/view`) },
            { path: "statements", Component: createAdminRedirectComponent("/reports/balance-sheet/view") },
            { path: "trial-balance", Component: createAdminRedirectComponent("/reports/trial-balance/view") },
            { path: "general-ledger", Component: createAdminRedirectComponent("/reports/general-ledger/configure") },
            { path: "journal", Component: createAdminRedirectComponent("/reports/journal-entries/view") },
            { path: "chart-of-accounts", Component: createAdminRedirectComponent("/reports/chart-of-accounts/view") },
          ],
        },
        {
          // Legacy accounting routes → Reports.
          path: "accounting",
          children: [
            { index: true, Component: createAdminRedirectComponent("/finance/reports") },
            { path: "chart", Component: createAdminRedirectComponent("/finance/reports/chart-of-accounts") },
            { path: "journal", Component: createAdminRedirectComponent("/finance/reports/journal") },
            { path: "ledger", Component: createAdminRedirectComponent("/finance/reports/general-ledger") },
            { path: "trial-balance", Component: createAdminRedirectComponent("/finance/reports/trial-balance") },
            { path: "statements", Component: createAdminRedirectComponent("/finance/reports/statements") },
          ],
        },
        {
          // Settings: finance configuration (categories, accounts, fiscal periods, opening balances,
          // reconciliation) as a nested shared layout + route-driven tabs.
          path: "settings",
          lazy: lazyFinanceSettingsLayoutRoute,
          children: [
            // Categories + accounts moved to the dedicated Chart of Accounts page; Settings now defaults to Setup.
            { index: true, Component: createAdminRedirectComponent("/finance/settings/setup") },
            {
              // The branch's accounting identity (IBA-119) — the books do not open until it is stated.
              path: "setup",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/settings/setup/page.tsx"),
            },
            {
              path: "accounts",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/settings/accounts/page.tsx"),
            },
            {
              // The branch's accounting currency (IBA-114).
              path: "currency",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/settings/currency/page.tsx"),
            },
            {
              // Fiscal year & period locking (IBA-72).
              path: "fiscal-periods",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/settings/fiscal-periods/page.tsx"),
            },
            {
              // Opening balances wizard (IBA-71).
              path: "opening-balances",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/settings/opening-balances/page.tsx"),
            },
            {
              // Branch-specific receipt identity, messaging and signature.
              path: "receipts",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/settings/receipts/page.tsx"),
            },
            {
              // MOVED (IBA-161): reconciliation is recurring work, not configuration, so it left Settings for
              // its own Finance child. Redirect rather than 404 — this URL is in people's history and docs.
              path: "reconciliation",
              Component: createAdminRedirectComponent("/finance/reconciliation"),
            },
          ],
        },
        {
          // Legacy setup routes → Settings.
          path: "setup",
          children: [
            { index: true, Component: createAdminRedirectComponent("/finance/settings") },
            { path: "periods", Component: createAdminRedirectComponent("/finance/settings/fiscal-periods") },
            {
              path: "opening-balances",
              Component: createAdminRedirectComponent("/finance/settings/opening-balances"),
            },
            {
              path: "reconciliation",
              Component: createAdminRedirectComponent("/finance/reconciliation"),
            },
          ],
        },
        {
          // Financial account detail — Overview + Reconciliation tabs (IBA-73).
          path: "accounts/:accountId",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/accounts/[accountId]/page.tsx"),
        },
        {
          // MOVED (IBA-161): one working surface, not two. The account tab and the standalone page would have
          // been the same screen reached two ways, which is how they drift apart.
          path: "accounts/:accountId/reconciliation",
          Component: createAdminRedirectComponent("/finance/reconciliation"),
        },
        {
          // No breadcrumb handle: `/finance/pledges` is a Finance nav item, so the section resolver already
          // names it (and `/finance/pledges/:id` inherits that trail by prefix).
          path: "pledges",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/pledges/page.tsx"),
        },
        {
          // The pledges page's tabs, each a real URL (the bare path is "in progress"). Static, so they rank
          // above `pledges/:id` and can never be read as an id.
          path: "pledges/late",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/pledges/page.tsx"),
        },
        {
          path: "pledges/fulfilled",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/pledges/page.tsx"),
        },
        {
          path: "pledges/all",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/pledges/page.tsx"),
        },
        {
          // Now the pledge overlay over the pledges list; `?activityId=` still preselects the activity.
          path: "pledges/create",
          Component: PledgeCreateRedirect,
        },
        {
          path: "pledges/:id",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/finance/pledges/[id]/page.tsx"),
        },
      ],
    },
    {
      /*
       * Reports — a top-level destination, not a Finance page.
       *
       * `:module` is the tab (all is the index; favorites and the module slugs are the rest), and it is a
       * REAL path segment so every tab is deep-linkable. It cannot collide with `:reportId`, which is only
       * ever reached with a second segment (`configure` / `view`).
       */
      path: "reports",
      children: [
        {
          lazy: lazyReportsLayoutRoute,
          children: [
            { index: true, lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/reports/page.tsx") },
            {
              path: ":reportId/configure",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/reports/[reportId]/configure/page.tsx"),
            },
            {
              path: ":reportId/view",
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/reports/[reportId]/view/page.tsx"),
            },
            /*
             * One STATIC route per tab, generated from the tab list — so adding a module still means a
             * registry entry and nothing else.
             *
             * They cannot be a single `:module` param. `/:ws/reports/members` and the branch route
             * `/:ws/:branchSlug/members` score identically under React Router's ranking (one static, two
             * dynamic each), and the branch set is declared first — so a dynamic tab segment lost every
             * three-part URL to a branch literally named "reports", and rendered a blank page.
             */
            ...REPORT_CATALOG_TABS.filter((tab) => tab !== ReportCatalogTab.All).map((tab) => ({
              path: tab,
              lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/reports/page.tsx"),
            })),
          ],
        },
      ],
    },
    {
      path: "budgets",
      // Budgets are finance plans evaluated against posted actuals, so the books must be configured before
      // either branch or parish/system budgets can be used.
      Component: FinanceSetupGuard,
      children: [
        {
          element: <RequireCapability capability="budgeting.view" />,
          children: [
            {
              lazy: lazyBudgetsOutletLayoutRoute,
              children: [
                {
                  lazy: lazyBudgetsShellLayoutRoute,
                  children: [
                    {
                      index: true,
                      lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/budgets/page.tsx"),
                    },
                    {
                      path: "list",
                      lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/budgets/list/page.tsx"),
                    },
                  ],
                },
                {
                  // Old budget create page: now the budget overlay over the budgets list.
                  path: "create",
                  Component: BudgetCreateRedirect,
                },
                {
                  path: ":id",
                  lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/budgets/[id]/page.tsx"),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      // Old minister create page: the form opens over the ministers list (Members → Ministries) instead.
      path: "ministers/create",
      Component: createAdminCreateRedirectComponent(() => CreateKind.Minister),
    },
    {
      path: "work-queue",
      handle: { breadcrumbKey: "churchNav.sections.workQueue.title" } satisfies TenantRouteHandle,
      element: <RequireCapability capability="workQueue.view" />,
      children: [
        {
          index: true,
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/work-queue/page.tsx"),
        },
        {
          // Each type tab is a real URL segment (`/work-queue/member-confirmations`) so it can be linked; the bare
          // path is the "All" tab. An unknown segment redirects there.
          path: ":tab",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/work-queue/page.tsx"),
        },
      ],
    },
    {
      // Global search: the phone's full search screen (a desktop is sent to the header palette instead).
      path: "search",
      handle: { breadcrumbKey: "search.palette.title" } satisfies TenantRouteHandle,
      lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/search/page.tsx"),
    },
    { path: "configuration", Component: createAdminRedirectComponent("/my-branch") },
    { path: "configuration/branch", Component: createAdminRedirectComponent("/my-branch") },
    { path: "people", Component: createAdminRedirectComponent("/members") },
    { path: "people/onboarding", Component: createAdminCreateRedirectComponent(() => CreateKind.Member) },
    { path: "people/roles", Component: createAdminRedirectComponent("/my-branch") },
    { path: "profile", Component: createStaticRedirectComponent("/") },
    {
      // Nested rather than two siblings so the detail route MATCHES its parent — that is what puts "Programs"
      // in front of the program's own name in the breadcrumb.
      path: "programmes",
      handle: { breadcrumbKey: "programs.list.title" } satisfies TenantRouteHandle,
      children: [
        {
          index: true,
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/programmes/page.tsx"),
        },
        {
          path: ":id",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/programmes/[id]/page.tsx"),
        },
      ],
    },
    { path: "roles", Component: createAdminRedirectComponent("/my-branch") },
    {
      // "My Branch": branch self-service (Overview + Team) inside the normal app shell. Replaces the old
      // nested Branch Settings sidebar. Visibility is gated in the sidebar (Branch ADMIN / workspace OWNER)
      // and the reused containers gate themselves; the backend is the enforcement boundary.
      path: "my-branch",
      // Its own tab bar: the section moved to the sidebar footer as a single link, so Overview ↔ Team has to
      // be navigable from inside the page rather than from a sidebar submenu.
      lazy: lazyMyBranchLayoutRoute,
      children: [
        {
          index: true,
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/my-branch/page.tsx"),
        },
        {
          path: "team",
          lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/my-branch/team/page.tsx"),
        },
      ],
    },
    {
      // Branchless `/:workspace/settings/*` is always workspace governance, including SINGLE_BRANCH workspaces.
      // Branch self-service stays under the branch route tree and now lives at "My Branch".
      path: "settings",
      lazy: lazyOrganizationSettingsLayoutRoute,
      children: createOrganizationSettingsRoutes(),
    },
    { path: "system/users", Component: createAdminRedirectComponent("/my-branch") },
    /**
     * Global Finance — workspace-wide finance, ORGANIZATION context only.
     *
     * Not registered for a branch, so `/:ws/:branch/global-finance` falls through to the 404: there is no
     * branch-scoped consolidated view. The layout route itself is the guard (multi-branch + parish finance
     * access), and the backend refuses the same two cases independently.
     *
     * `global-finance` is a RESERVED branch slug on both sides — without that, `/acme/global-finance` parses
     * as a branch named "global-finance" and every page inside resolves the wrong scope.
     */
    ...(context === "organization"
      ? ([
          {
            path: "global-finance",
            children: [
              { index: true, Component: createAdminRedirectComponent("/global-finance/overview") },
              {
                lazy: lazyGlobalFinanceLayoutRoute,
                children: [
                  {
                    path: "overview",
                    lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/global-finance/overview/page.tsx"),
                  },
                  {
                    path: "chart-of-accounts",
                    lazy: lazyPage(
                      "../[workspaceSlug]/[branchSlug]/admin/global-finance/chart-of-accounts/page.tsx",
                    ),
                  },
                  {
                    path: "funds",
                    lazy: lazyPage("../[workspaceSlug]/[branchSlug]/admin/global-finance/funds/page.tsx"),
                  },
                  {
                    // Global Finance no longer has its own Reports destination either. Consolidated runs are
                    // a SCOPE the reader chooses on the report, not a separate place to look.
                    path: "reports",
                    children: [
                      { index: true, Component: createAdminRedirectComponent("/reports/finance") },
                      { path: ":reportId/configure", Component: createAdminParamRedirectComponent((p) => `/reports/${p.reportId}/configure`) },
                      { path: ":reportId/view", Component: createAdminParamRedirectComponent((p) => `/reports/${p.reportId}/view`) },
                    ],
                  },
                ],
              },
            ],
          },
          // The moved pages keep working from their old parish URLs rather than 404ing on a stale bookmark.
          { path: "finance/accounts", Component: createAdminRedirectComponent("/global-finance/chart-of-accounts") },
          { path: "finance/funds", Component: createAdminRedirectComponent("/global-finance/funds") },
        ] satisfies RouteObject[])
      : []),
  ]
}

function createBranchScopedAdminRouteSet(context: AdminRouteContext = "branch"): RouteObject {
  return {
    lazy: lazyAdminLayoutRoute,
    children: createAdminBranchChildren(context),
  }
}

export function createTenantRootChildren(): RouteObject[] {
  return [
    {
      lazy: lazyAuthLayoutRoute,
      children: [
        // The explicit step segment outranks the dynamic /:workspaceSlug/:branchSlug route. Without it,
        // Clerk paths such as /register/verify-email-address are interpreted as a workspace + branch and
        // mount the authenticated app shell instead of the registration flow.
        { path: "/login/:clerkStep/*", lazy: lazyPage("../(auth)/login/[[...login]]/page.tsx") },
        { path: "/login/*", lazy: lazyPage("../(auth)/login/[[...login]]/page.tsx") },
        { path: "/register/:clerkStep/*", lazy: lazyPage("../(auth)/register/[[...register]]/page.tsx") },
        { path: "/register/*", lazy: lazyPage("../(auth)/register/[[...register]]/page.tsx") },
      ],
    },
    { path: "/auth/failure", lazy: lazyPage("../auth/failure/page.tsx") },
    { path: "/accept-invitation", lazy: lazyPage("../accept-invitation/page.tsx") },
    // Product-led onboarding (ADR-0020): pre-auth church-setup wizard + post-auth draft claim. Both public.
    { path: "/create-church", lazy: lazyPage("../create-church/page.tsx") },
    // The marketing website links here to start onboarding — forward it to the wizard (ADR-0020).
    // Legacy marketing deep link — the church form now lives behind auth at /create-church.
    { path: "/register-church", Component: createStaticRedirectComponent("/create-church") },
    { path: "/account-blocked", lazy: lazyPage("../account-blocked/page.tsx") },
    { path: "/auth/callback/*", lazy: lazyPage("../auth/callback/[[...callback]]/page.tsx") },
    // Internal tools (UI showcase, legacy JSON form builder): dev builds only, never shipped as public pages.
    ...(import.meta.env.DEV
      ? [
          { path: "/components-showcase", lazy: lazyPage("../components-showcase/page.tsx") },
          { path: "/form-builder", lazy: lazyPage("../form-builder/page.tsx") },
        ]
      : []),
    { path: "/dashboard", lazy: lazyPage("../dashboard/page.tsx") },
    { path: "/no-access", lazy: lazyPage("../no-access/page.tsx") },
    { path: "/not-found", lazy: lazyNotFoundRoute },
    { path: "/", lazy: lazyPage("../tenant-entry/page.tsx") },
    { path: "/subscription", Component: createStaticRedirectComponent("/dashboard") },
    { path: "/workspaces", lazy: lazyPage("../workspace-selector/page.tsx") },
    { path: "/workspace-selector", Component: createStaticRedirectComponent("/workspaces") },
    // Profile now lives as a tab inside Clerk "Manage account" — the standalone /profile page was removed,
    // so these legacy paths redirect home.
    { path: "/profile", Component: createStaticRedirectComponent("/") },
    { path: "/profile/preferences", Component: createStaticRedirectComponent("/") },
    { path: "/profile/privacy", Component: createStaticRedirectComponent("/") },
    // Old links were accidentally produced while Settings occupied React Router's dynamic branch slot.
    // Keep bookmarks working, but never let `settings` become the apparent branch context again.
    {
      path: "/:workspaceSlug/settings/branches/:branchId/:tab?",
      lazy: lazyLegacyBranchDetailRedirect,
    },
    {
      // Branch context — `/:workspaceSlug/:branchSlug/...` (flat URL model, IBA-155). Full-page form designer
      // and report builder mount outside the admin layout (whole viewport); everything else uses the app shell.
      path: "/:workspaceSlug/:branchSlug",
      children: [
        {
          path: "form-designer",
          element: <RequireCapability capability="forms.view" />,
          children: [{ index: true, lazy: lazyPage("../[workspaceSlug]/[branchSlug]/form-designer/page.tsx") }],
        },
        createBranchScopedAdminRouteSet("branch"),
      ],
    },
    {
      // Organization context — `/:workspaceSlug`. The workspace root IS the Organization landing (parish/SYSTEM
      // scope): Overview, Branches, Activities, Ministries, Finance, Budgets, Audit Log, Settings. Reuses the
      // app shell in parish mode; branch-scoped Organization data resolves to the SYSTEM branch server-side.
      // React-router ranks the static Organization sub-routes (e.g. `/:ws/finance`) above the dynamic branch
      // slot (`/:ws/:branchSlug`), so a branch may never be named after a reserved Organization/admin route.
      path: "/:workspaceSlug",
      children: [createBranchScopedAdminRouteSet("organization")],
    },
    { path: "*", lazy: lazyNotFoundRoute },
  ]
}
